import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  localBusinessJsonLd,
  productJsonLd,
  videoJsonLd,
} from '../PageSeo';
import { createWhatsAppUrl } from '../siteConfig';
import VideoShowcase from '../VideoShowcase';

// Kerangka artikel blog untuk satu model: hero, angka cepat, isi artikel,
// tabel spesifikasi, galeri foto, FAQ, tautan terkait, dan CTA WhatsApp.
// Setiap artikel cukup mengirim satu objek `article` (lihat Ti7Article.jsx).

function ModelArticle({ article }) {
  const {
    slug,
    metaTitle,
    metaDescription,
    keywords,
    datePublished,
    dateModified,
    heroImage,
    heroAlt,
    eyebrow,
    heroTitle,
    heroText,
    modelPath,
    modelLabel,
    waMessage,
    quickSpecs = [],
    sections = [],
    specTitle,
    specRows = [],
    specNote,
    galleryTitle,
    gallery = [],
    faqs = [],
    related = [],
    ctaTitle,
    ctaText,
    product,
    video,
  } = article;

  const path = `/blog/${slug}`;
  const waLink = createWhatsAppUrl(waMessage);
  const faqJsonLd = faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      }
    : null;
  const productLd = product
    ? productJsonLd({ ...product, image: heroImage, path })
    : null;
  const videoLd = video
    ? videoJsonLd({
        name: video.name,
        description: video.description,
        thumbnail: video.poster,
        contentUrl: video.src,
        uploadDate: video.uploadDate,
        durationSeconds: video.durationSeconds,
      })
    : null;

  return (
    <>
      <PageSeo
        title={metaTitle}
        description={metaDescription}
        keywords={keywords}
        path={path}
        image={heroImage}
        type='article'
        jsonLd={[
          localBusinessJsonLd(),
          articleJsonLd({
            title: metaTitle,
            description: metaDescription,
            image: heroImage,
            path,
            datePublished,
            dateModified: dateModified || datePublished,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: heroTitle, path },
          ]),
          faqJsonLd,
          productLd,
          videoLd,
        ].filter(Boolean)}
      />

      <Box component='main' sx={{ backgroundColor: '#f4f7fb', overflow: 'hidden' }}>
        {/* Hero */}
        <Box
          component='section'
          aria-labelledby='article-title'
          sx={{
            position: 'relative',
            minHeight: { xs: '80vh', md: '90vh' },
            display: 'flex',
            alignItems: 'flex-end',
            pt: { xs: 10, md: 11 },
            backgroundColor: '#111827',
          }}
        >
          <Box
            component='img'
            src={heroImage}
            alt={heroAlt}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.84) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: '100%',
              maxWidth: 1180,
              mx: 'auto',
              px: { xs: 2, md: 5 },
              pb: { xs: 5, md: 7 },
              color: '#fff',
            }}
          >
            <Typography sx={styles.eyebrowDark}>{eyebrow}</Typography>
            <Typography id='article-title' component='h1' sx={styles.heroTitle}>
              {heroTitle}
            </Typography>
            <Typography sx={styles.heroText}>{heroText}</Typography>
            <Typography sx={{ mt: 1.5, fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' }}>
              Ditulis oleh Junius, BYD BSD Serpong · Diperbarui {formatDate(dateModified || datePublished)}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button href={waLink} target='_blank' rel='noopener noreferrer' variant='contained' sx={styles.buttonPrimary}>
                Chat WhatsApp Junius
              </Button>
              {modelPath && (
                <Button component={RouterLink} to={modelPath} variant='outlined' sx={styles.buttonGhost}>
                  {modelLabel || 'Lihat halaman model'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Angka cepat */}
        {quickSpecs.length > 0 && (
          <Box component='section' sx={{ px: { xs: 2, md: 6 }, pt: { xs: 4, md: 5 } }}>
            <Box
              sx={{
                maxWidth: 1180,
                mx: 'auto',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${Math.min(quickSpecs.length, 5)}, 1fr)` },
                gap: 1.5,
              }}
            >
              {quickSpecs.map((spec) => (
                <Box key={spec.label} sx={styles.infoCard}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#667085', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {spec.label}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 900, color: '#111827' }}>
                    {spec.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Video */}
        {video && (
          <Box sx={{ px: { xs: 2, md: 6 }, pt: { xs: 4, md: 5 } }}>
            <Box sx={{ maxWidth: 1180, mx: 'auto', borderRadius: '8px', overflow: 'hidden' }}>
              <VideoShowcase
                id='video-artikel'
                tone='dark'
                eyebrow={video.eyebrow}
                title={video.title}
                description={video.description}
                src={video.src}
                poster={video.poster}
                ariaLabel={video.ariaLabel}
                durationSeconds={video.durationSeconds}
                chapters={video.chapters}
                note={video.note}
                cta={{ label: 'Chat WhatsApp Junius', href: waLink }}
              />
            </Box>
          </Box>
        )}

        {/* Isi artikel */}
        <Box component='section' sx={{ px: { xs: 2, md: 6 }, py: { xs: 5, md: 7 } }}>
          <Box component='article' sx={{ maxWidth: 860, mx: 'auto' }}>
            {sections.map((section) => (
              <Box key={section.title} sx={{ mb: { xs: 4, md: 5 } }}>
                <Typography component='h2' sx={styles.sectionTitle}>
                  {section.title}
                </Typography>
                {section.paragraphs.map((text) => (
                  <Typography key={text.slice(0, 40)} sx={{ ...styles.bodyText, mt: 1.5 }}>
                    {text}
                  </Typography>
                ))}
                {section.bullets && (
                  <Box component='ul' sx={{ mt: 1.5, pl: 2.5, ...styles.bodyText }}>
                    {section.bullets.map((item) => (
                      <Box component='li' key={item} sx={{ mb: 0.8 }}>
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Tabel spesifikasi */}
        {specRows.length > 0 && (
          <Box component='section' sx={{ px: { xs: 2, md: 6 }, pb: { xs: 5, md: 7 } }}>
            <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
              <Typography sx={styles.eyebrow}>Spesifikasi</Typography>
              <Typography component='h2' sx={styles.sectionTitleLg}>
                {specTitle}
              </Typography>
              <Box
                component='table'
                sx={{
                  mt: 3,
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid rgba(17,24,39,0.08)',
                  '& td': { px: 2, py: 1.3, borderTop: '1px solid rgba(17,24,39,0.08)', fontSize: '0.97rem', verticalAlign: 'top' },
                  '& tr:first-of-type td': { borderTop: 0 },
                  '& td:first-of-type': { color: '#4b5563', width: '42%' },
                  '& td:last-of-type': { fontWeight: 700, color: '#111827' },
                }}
              >
                <tbody>
                  {specRows.map(([label, value]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
              {specNote && (
                <Typography sx={{ mt: 1.5, fontSize: '0.85rem', color: '#6b7280' }}>{specNote}</Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Galeri */}
        {gallery.length > 0 && (
          <Box component='section' sx={{ px: { xs: 2, md: 6 }, pb: { xs: 5, md: 7 } }}>
            <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
              <Typography sx={styles.eyebrow}>Galeri</Typography>
              <Typography component='h2' sx={styles.sectionTitleLg}>
                {galleryTitle}
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${Math.min(gallery.length, 3)}, 1fr)` },
                  gap: 2,
                }}
              >
                {gallery.map((item) => (
                  <Box key={item.src} sx={styles.imageCard}>
                    <Box
                      sx={{
                        position: 'relative',
                        background: item.bg || '#e5e7eb',
                        '&::before': { content: '""', display: 'block', paddingTop: `${item.ratio || 66}%` },
                      }}
                    >
                      <Box
                        component='img'
                        src={item.src}
                        alt={item.alt}
                        loading='lazy'
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: item.fit || 'cover',
                          p: item.fit === 'contain' ? 2 : 0,
                          boxSizing: 'border-box',
                          display: 'block',
                        }}
                      />
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 900, color: '#111827' }}>{item.title}</Typography>
                      <Typography sx={{ mt: 0.5, color: '#4b5563', lineHeight: 1.6, fontSize: '0.95rem' }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* FAQ */}
        {faqs.length > 0 && (
          <Box component='section' sx={{ px: { xs: 2, md: 6 }, pb: { xs: 5, md: 7 } }}>
            <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
              <Typography sx={styles.eyebrow}>FAQ</Typography>
              <Typography component='h2' sx={styles.sectionTitleLg}>
                Pertanyaan yang sering diajukan
              </Typography>
              <Box sx={{ mt: 3, display: 'grid', gap: 1.5 }}>
                {faqs.map((item) => (
                  <Box key={item.q} sx={styles.infoCard}>
                    <Typography component='h3' sx={{ fontSize: '1.08rem', fontWeight: 900, color: '#111827' }}>
                      {item.q}
                    </Typography>
                    <Typography sx={{ ...styles.bodyText, mt: 0.8 }}>{item.a}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Tautan terkait */}
        {related.length > 0 && (
          <Box component='section' sx={{ px: { xs: 2, md: 6 }, pb: { xs: 5, md: 7 } }}>
            <Box sx={{ maxWidth: 1080, mx: 'auto', ...styles.infoCard }}>
              <Typography component='h2' sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#111827' }}>
                Baca juga
              </Typography>
              <Box component='ul' sx={{ mt: 1.5, pl: 2.5, mb: 0, ...styles.bodyText }}>
                {related.map((link) => (
                  <Box component='li' key={link.to} sx={{ mb: 0.6 }}>
                    <Box component={RouterLink} to={link.to} sx={{ color: '#1f5fd1', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {link.label}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* CTA */}
        <Box component='section' sx={{ px: { xs: 2, md: 6 }, pb: { xs: 7, md: 9 } }}>
          <Box
            sx={{
              maxWidth: 1080,
              mx: 'auto',
              borderRadius: '8px',
              backgroundColor: '#111827',
              color: '#fff',
              px: { xs: 2.5, md: 4 },
              py: { xs: 3.5, md: 4.5 },
              textAlign: 'center',
            }}
          >
            <Typography component='h2' sx={styles.sectionTitleDark}>
              {ctaTitle}
            </Typography>
            <Typography sx={{ mt: 1.5, maxWidth: 760, mx: 'auto', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8 }}>
              {ctaText}
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button href={waLink} target='_blank' rel='noopener noreferrer' variant='contained' sx={styles.buttonPrimary}>
                Chat WhatsApp Junius
              </Button>
              <Button component={RouterLink} to='/pricing' variant='outlined' sx={styles.buttonGhost}>
                Cek harga model lain
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

function formatDate(iso) {
  const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${months[m - 1]} ${y}`;
}

const styles = {
  eyebrow: {
    color: '#4c5867',
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontSize: '0.82rem',
  },
  eyebrowDark: {
    display: 'inline-flex',
    px: 1.2,
    py: 0.65,
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    fontSize: '0.78rem',
    fontWeight: 900,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  heroTitle: {
    mt: 1.6,
    fontSize: { xs: '2.1rem', sm: '3rem', md: '4.2rem' },
    fontWeight: 900,
    lineHeight: 1,
    maxWidth: 900,
  },
  heroText: {
    mt: 1.5,
    maxWidth: 760,
    fontSize: { xs: '1rem', md: '1.08rem' },
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.9)',
  },
  sectionTitle: {
    fontSize: { xs: '1.5rem', md: '1.9rem' },
    fontWeight: 900,
    color: '#111827',
    lineHeight: 1.15,
  },
  sectionTitleLg: {
    mt: 1,
    fontSize: { xs: '1.8rem', md: '2.6rem' },
    fontWeight: 900,
    color: '#111827',
    lineHeight: 1.05,
  },
  sectionTitleDark: {
    fontSize: { xs: '1.8rem', md: '2.6rem' },
    fontWeight: 900,
    lineHeight: 1.05,
  },
  bodyText: {
    color: '#4b5563',
    lineHeight: 1.8,
    fontSize: { xs: '1rem', md: '1.05rem' },
  },
  infoCard: {
    p: 2.4,
    borderRadius: '8px',
    backgroundColor: '#fff',
    border: '1px solid rgba(17,24,39,0.08)',
    boxShadow: '0 12px 26px rgba(16,24,40,0.06)',
  },
  imageCard: {
    overflow: 'hidden',
    borderRadius: '8px',
    backgroundColor: '#fff',
    border: '1px solid rgba(17,24,39,0.08)',
    boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
  },
  buttonPrimary: {
    px: 3.2,
    py: 1.1,
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 900,
    backgroundColor: '#1f5fd1',
    '&:hover': { backgroundColor: '#184ca8' },
  },
  buttonGhost: {
    px: 3.2,
    py: 1.1,
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 900,
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.68)',
    '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
  },
};

export default ModelArticle;
